// src/services/royaltyService.ts
// Licensing Royalty operations via Backend API

import { config } from '../config';
import { auth } from './firebase';

// Helper to get auth token
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

// API Response types

export interface RoyaltyRecord {
  id: string;
  productId: string;
  orderId: string;
  characterId?: string;
  characterName?: string;
  licensorId?: string;
  creatorId?: string;
  orderAmount: number;
  royaltyAmount: number;
  royaltyPercentage: number;
  status: 'pending' | 'completed' | 'failed' | 'simulated';
  createdAt?: string;
  completedAt?: string;
  // Display fields
  creatorName?: string;
  licensorName?: string;
}

export interface RoyaltyListResponse {
  royalties: RoyaltyRecord[];
  hasMore: boolean;
  totalAmount: number;
}

export interface RoyaltySummaryResponse {
  totalReceived: number;   // 受け取った合計金額
  totalPaid: number;       // 支払った合計金額
  receivedCount: number;   // 受け取った件数
  paidCount: number;       // 支払った件数
  pendingReceived: number; // 未処理（受け取り予定）
  pendingPaid: number;     // 未処理（支払い予定）
}

// ========== Royalty API Operations ==========

/**
 * Get royalties received by the current user
 * ライセンサーが受け取ったロイヤリティ一覧を取得
 */
export const getReceivedRoyalties = async (
  limit: number = 20,
  before?: string
): Promise<RoyaltyListResponse> => {
  const token = await getAuthToken();

  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (before) params.append('before', before);

  const url = `${config.apiBaseUrl}/api/orifoli/licensing/royalties/received?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get received royalties: ${errorText}`);
  }

  return response.json();
};

/**
 * Get royalties paid by the current user
 * 商品作成者が支払ったロイヤリティ一覧を取得
 */
export const getPaidRoyalties = async (
  limit: number = 20,
  before?: string
): Promise<RoyaltyListResponse> => {
  const token = await getAuthToken();

  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (before) params.append('before', before);

  const url = `${config.apiBaseUrl}/api/orifoli/licensing/royalties/paid?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get paid royalties: ${errorText}`);
  }

  return response.json();
};

/**
 * Get royalty summary for the current user
 * ロイヤリティサマリーを取得
 */
export const getRoyaltySummary = async (): Promise<RoyaltySummaryResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${config.apiBaseUrl}/api/orifoli/licensing/summary`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get royalty summary: ${errorText}`);
  }

  return response.json();
};

// ========== Helper Functions ==========

/**
 * Format royalty amount for display
 * @param amount Amount in yen
 */
export const formatRoyaltyAmount = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

/**
 * Format date for display
 * @param dateStr ISO date string
 */
export const formatRoyaltyDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';

  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};
